import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, Search } from 'lucide-react';
import { CaretDown } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import { inputRecipe, selectTriggerRecipe } from '../recipes/input.recipe';
import {
  optionSheetCheckRecipe,
  optionSheetItemRecipe,
  optionSheetSearchInputRecipe,
  optionSheetSearchRecipe,
} from '../recipes/sheet.recipe';
import { useFieldControlProps } from './field-context';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  BottomSheetTrigger,
} from './BottomSheet';
import { Button } from './Button';
import type { SelectOption } from './Select';
import { Text } from './Text';

export interface OptionSheetProps {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  /** Shown in the field until something is chosen. */
  placeholder?: string;
  /** The sheet's heading — usually the field's label: "Priority". */
  title: string;
  /**
   * A search box along the bottom, and the sheet at full height. For a list
   * long enough to scroll — vehicle makes, accounts — not for four
   * priorities, where a search box is one more thing to read past.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Said when a search matches nothing. */
  emptyMessage?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  /**
   * What opens the sheet, in place of the field — a status pill on a call's
   * footer. A Button or anything that forwards its ref; it is the sheet's
   * trigger, so it opens on a tap without an onClick of its own.
   */
  trigger?: React.ReactNode;
  /** Open state, for a screen that has to close the sheet itself later. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * A button along the foot that closes the sheet: "Done" under the call's
   * statuses, as the Figma draws it. A choice still takes effect on the tap;
   * this is the way out for someone who looked and kept what they had, so
   * a sheet with one has no close button in its header.
   */
  doneLabel?: string;
  /**
   * `rows` lists the options as words. `chips` is for options that are
   * chips already — StatusPicker's statuses — drawn as a column of them
   * with the tick at the far end of the current one's row.
   */
  layout?: 'rows' | 'chips';
  /**
   * Take what was typed when no option says it, for a field no list can
   * complete: a call's reason. Needs `searchable`, since the search box is
   * where it is typed; the list then ends with a row that uses the words as
   * they are. A value that is not one of the options shows as itself.
   */
  allowCustom?: boolean;
  /**
   * Normalise what was typed — `(v) => v.toUpperCase()`, as the dashboard
   * saves a custom reason. Casing is the product's rule, not the control's.
   */
  formatCustomValue?: (value: string) => string;
}

/** The custom row's cmdk value; never reaches `onValueChange`. */
const CUSTOM_VALUE = '__custom__';

/**
 * Select, for a phone: the field looks the same, but the options rise in a
 * bottom sheet instead of dropping from the field.
 *
 * A popover list under a field is sized for a pointer. On a phone it opens
 * wherever the field happens to be, often half off the screen, with 14px rows
 * under a thumb. The sheet puts the list where the thumb is and makes the rows
 * big enough to hit.
 *
 * Takes the same props as Select's `options` form, so moving a field across
 * is a rename — and inside a FormField it is wired the same way:
 *
 *   <FormField form={form} name="priority" label="Priority">
 *     {(field) => (
 *       <OptionSheet
 *         title="Priority"
 *         options={PRIORITIES}
 *         value={field.value}
 *         onValueChange={field.onChange}
 *       />
 *     )}
 *   </FormField>
 *
 * Opened from inside a BottomSheet it stacks over it, like any other sheet.
 * Give it a `trigger` and that opens it instead of a field: StatusPicker's
 * sheet form is one. With `allowCustom` it takes a value off the list too,
 * typed in its search box, as Combobox does on a desktop.
 */
export function OptionSheet({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  title,
  searchable = false,
  searchPlaceholder = 'Search',
  emptyMessage = 'Nothing matches that.',
  id,
  disabled,
  required,
  className,
  trigger,
  open: openProp,
  onOpenChange,
  doneLabel,
  layout = 'rows',
  allowCustom = false,
  formatCustomValue,
}: OptionSheetProps) {
  const fieldProps = useFieldControlProps({ id, disabled, required });
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const [search, setSearch] = React.useState('');
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
    // Each opening starts from the whole list, not the last search.
    if (!next) setSearch('');
  };

  // A custom value chosen before has to show as the choice, and be ticked
  // as the current one when the sheet opens again.
  const listed =
    allowCustom && value && !options.some((option) => option.value === value)
      ? [{ value, label: value }, ...options]
      : options;
  const selected = listed.find((option) => option.value === value);

  const typed = search.trim();
  const custom =
    allowCustom && typed ? (formatCustomValue?.(typed) ?? typed) : '';
  // Typing an option's own words picks that option; offering them again as
  // a custom value would save the same reason two ways.
  const offerCustom =
    custom !== '' &&
    !listed.some(
      (option) =>
        option.value === custom ||
        (typeof option.label === 'string' &&
          option.label.toLowerCase() === typed.toLowerCase())
    );

  const choose = (next: string) => {
    onValueChange?.(next);
    setOpen(false);
  };

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            id={fieldProps.id}
            disabled={fieldProps.disabled}
            aria-describedby={fieldProps['aria-describedby']}
            aria-invalid={fieldProps['aria-invalid']}
            aria-required={fieldProps.required || undefined}
            // Select's trigger reads this to mute the placeholder; the same
            // attribute keeps the two fields looking the same.
            data-placeholder={selected ? undefined : ''}
            className={cn(inputRecipe(), selectTriggerRecipe(), className)}
          >
            <span>{selected ? selected.label : placeholder}</span>
            {/* The Figma's caret: Phosphor's, 20px, in ink. */}
            <CaretDown className="size-icon-md shrink-0" aria-hidden="true" />
          </button>
        )}
      </BottomSheetTrigger>

      <BottomSheetContent
        title={title}
        size={searchable ? 'full' : 'auto'}
        hideCloseButton={doneLabel ? true : undefined}
        footer={
          doneLabel ? (
            <BottomSheetClose asChild>
              <Button size="xl" pill>
                {doneLabel}
              </Button>
            </BottomSheetClose>
          ) : undefined
        }
      >
        {/* defaultValue, not value: it starts the highlight on the current
            choice, and a controlled value would pin it there against the
            arrow keys. */}
        <CommandPrimitive label={title} defaultValue={value} loop>
          <CommandPrimitive.List>
            {offerCustom ? null : (
              <CommandPrimitive.Empty>
                <Text variant="body" tone="muted">
                  {emptyMessage}
                </Text>
              </CommandPrimitive.Empty>
            )}
            {listed.map((option) => (
              <CommandPrimitive.Item
                key={option.value}
                // cmdk filters on `value`, which here is an id; the label is
                // what someone searching for "Chevy" is actually typing at.
                value={option.value}
                keywords={
                  typeof option.label === 'string' ? [option.label] : undefined
                }
                disabled={option.disabled}
                onSelect={choose}
                data-current={option.value === value || undefined}
                className={optionSheetItemRecipe({ layout })}
              >
                {option.label}
                {option.value === value ? (
                  <>
                    <Check
                      aria-hidden="true"
                      className={optionSheetCheckRecipe({ layout })}
                    />
                    {/* cmdk spends aria-selected on the highlight, so the
                        chosen option has to say so in words. */}
                    <span className="sr-only">(current)</span>
                  </>
                ) : null}
              </CommandPrimitive.Item>
            ))}
            {offerCustom ? (
              // Mounted whatever the search, since cmdk would filter it out
              // for not matching the words it is made of. It sorts last,
              // next to the search box the words were typed in.
              <CommandPrimitive.Item
                forceMount
                value={CUSTOM_VALUE}
                onSelect={() => choose(custom)}
                className={optionSheetItemRecipe({ layout })}
              >
                {`Use “${custom}”`}
              </CommandPrimitive.Item>
            ) : null}
          </CommandPrimitive.List>

          {searchable ? (
            <div className={optionSheetSearchRecipe()}>
              <Search className="size-icon-sm shrink-0" aria-hidden="true" />
              <CommandPrimitive.Input
                value={search}
                onValueChange={setSearch}
                placeholder={searchPlaceholder}
                className={optionSheetSearchInputRecipe()}
              />
            </div>
          ) : null}
        </CommandPrimitive>
      </BottomSheetContent>
    </BottomSheet>
  );
}
